import { POSSIBLE } from './prompts.js';

/**
 * What your body can do where you are standing: get under something, or get
 * up something. Two prompts and the one frame of state that getting under
 * something needs. Nothing here talks to anybody; that is `conversation.js`.
 *
 * `reading` is whether the sheet is up, asked rather than held, because the
 * sheet is somebody else's state.
 */
export function createBodyOffers({ concealment, climbing, wheeled, player, rig, reading = () => false }) {
  /**
   * Getting out of sight.
   *
   * Crouch — held, not tapped, because getting under something takes a moment and
   * the moment is the cost. Letting go once you are in gets you out again: the
   * same key, the same idea, no second binding.
   */
  let hiding = false;

  function runConceal(dt) {
    // The state machine runs every frame whatever the player is doing; what the
    // action key offers about it is `offerConceal` below. Getting into something
    // takes a moment and the moment is still the cost.
    const result = concealment.update(dt, {
      position: player.position,
      wanted: hiding && !reading() && !climbing.busy && !wheeled.active,
    });
    // Getting into something means being in it. Same as the cart and the ledge:
    // one thing owns the body while it owns it.
    if (result.in) player.teleport(result.in);
    if (!result.near && !concealment.hidden) hiding = false;
    return result;
  }

  function offerConceal(result) {
    const out = [];
    if (concealment.hidden) {
      out.push({
        id: 'conceal-out',
        tier: POSSIBLE,
        verb: 'come out from',
        what: concealment.hidden.label,
        run: () => {
          hiding = false;
        },
      });
      return out;
    }
    if (!result?.near) return out;
    out.push({
      id: 'conceal',
      tier: POSSIBLE,
      verb: 'get',
      what: result.near.label,
      run: () => {
        hiding = true;
      },
    });
    return out;
  }

  /**
   * Getting off the floor.
   *
   * Space, because a climb is what a jump turns into when there is something in
   * front of you at the right height. There is no climb key and no climb prompt
   * on any particular object — the prompt appears wherever the geometry says it
   * can, which is how the dock rail, the scaffold and a stack of crates all got
   * climbable without anybody deciding they should be.
   */
  function offerClimb() {
    const out = [];
    if (climbing.state === 'hanging') {
      out.push({
        id: 'climb-up',
        tier: POSSIBLE,
        verb: 'pull up',
        what: 'onto the ledge',
        run: () => climbing.mantle(player.position),
      });
      out.push({
        id: 'climb-drop',
        tier: POSSIBLE,
        verb: 'let go of',
        what: 'the ledge',
        run: () => climbing.release(),
      });
      return out;
    }
    if (climbing.busy) return out;

    // Not with a cart in your hands, and not while reading. Both of those are
    // already using the hands this would need.
    if (wheeled.active || reading()) return out;

    const edge = climbing.find(player.position, rig.forward, player.position.y);
    if (!edge) return out;

    out.push({
      id: 'climb',
      tier: POSSIBLE,
      verb: edge.kind === 'mantle' ? 'climb' : 'grab',
      what: edge.kind === 'mantle' ? 'up' : 'the edge',
      run: () => climbing.grab(edge, player.position),
    });
    return out;
  }

  return { runConceal, offerConceal, offerClimb };
}
